import {useEffect, useEffectEvent, type RefObject} from 'react'

export interface ChatComposerReadiness {
  readonly observationError?: 'visibility_unsupported' | 'visibility_failed'
  readonly connected: boolean
  readonly editable: boolean
  readonly editHandlerAttached: boolean
  readonly actionHandlerAttached: boolean
  readonly validInputActionEnabled: boolean
  readonly mutationActionsDisabled: boolean
}

export function useChatComposerReadiness(
  onReadiness: ((readiness: ChatComposerReadiness) => void) | undefined,
  textAreaRef: RefObject<HTMLTextAreaElement | null>,
  readOnly: boolean,
  isSubmitting: boolean,
  actionHandlerAttached: boolean,
  validInputActionEnabled: boolean,
) {
  const observeCommittedControls = useEffectEvent(() => {
    if (!onReadiness) return
    const input = textAreaRef.current
    let visible = false
    let observationError: ChatComposerReadiness['observationError']
    if (input?.isConnected && !input.closest('[hidden], [inert]')) {
      try {
        if (typeof input.checkVisibility === 'function') {
          visible = input.checkVisibility({checkOpacity: true, checkVisibilityCSS: true})
        } else {
          observationError = 'visibility_unsupported'
        }
      } catch {
        observationError = 'visibility_failed'
      }
    }
    onReadiness({
      connected: visible,
      ...(observationError ? {observationError} : {}),
      editable: Boolean(input && !input.readOnly && !input.disabled),
      editHandlerAttached: !readOnly,
      actionHandlerAttached,
      validInputActionEnabled,
      mutationActionsDisabled: readOnly,
    })
  })
  useEffect(() => {
    observeCommittedControls()
  }, [onReadiness, textAreaRef, readOnly, isSubmitting, actionHandlerAttached, validInputActionEnabled])
}
